import React, { useEffect, useState } from "react";
import i18next from "i18next";
import { reisorProMarketRequest } from "../utils/reisorProMarketHttp.js";
import ReisorProExpeditorParkForm from "./ReisorProExpeditorParkForm.jsx";

const COPY = {
  ru: { title: "Мой парк", subtitle: "Водители и машины", empty: "Парк пока пуст", error: "Не удалось загрузить парк.", freeAt: "Будет свободен", place: "Где", reminder: "Напомнить", noReminder: "нет", hourUnit: "ч", call: "Позвонить", whatsapp: "WhatsApp", telegram: "Telegram", add: "Добавить", edit: "Изменить", publish: "Опубликовать", archive: "Убрать из парка", archiveConfirm: "Убрать водителя и машину из парка?", archiveError: "Не удалось убрать из парка." },
  uz: { title: "Менинг парким", subtitle: "Ҳайдовчилар ва машиналар", empty: "Парк ҳозирча бўш", error: "Паркни юклаб бўлмади.", freeAt: "Бўшайди", place: "Қаерда", reminder: "Эслатма", noReminder: "йўқ", hourUnit: "соат", call: "Қўнғироқ", whatsapp: "WhatsApp", telegram: "Telegram", add: "Қўшиш", edit: "Ўзгартириш", publish: "Эълон қилиш", archive: "Паркдан олиб ташлаш", archiveConfirm: "Ҳайдовчи ва машинани паркдан олиб ташлайсизми?", archiveError: "Паркдан олиб ташлаб бўлмади." },
  tr: { title: "Filom", subtitle: "Sürücüler ve araçlar", empty: "Filo henüz boş", error: "Filo yüklenemedi.", freeAt: "Müsait olacak", place: "Nerede", reminder: "Hatırlatma", noReminder: "yok", hourUnit: "sa", call: "Ara", whatsapp: "WhatsApp", telegram: "Telegram", add: "Ekle", edit: "Düzenle", publish: "Yayınla", archive: "Filodan çıkar", archiveConfirm: "Sürücü ve araç filodan çıkarılsın mı?", archiveError: "Filodan çıkarılamadı." },
};

function lang() {
  const value = String(i18next.language || "ru").toLowerCase();
  if (value.startsWith("uz")) return "uz";
  if (value.startsWith("tr")) return "tr";
  return "ru";
}

function formatDate(value, code) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const locale = code === "uz" ? "uz-UZ" : code === "tr" ? "tr-TR" : "ru-RU";
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function phoneDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

function telegramUrl(value) {
  const username = String(value || "").trim().replace(/^@+/, "");
  return username ? `https://t.me/${encodeURIComponent(username)}` : "";
}

export default function ReisorProExpeditorPark({
  token,
  onPreparePublish,
}) {
  const code = lang();
  const t = COPY[code] || COPY.ru;
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function archiveItem(id) {
    if (!id || !window.confirm(t.archiveConfirm)) return;
    setError("");
    try {
      const { response, data } = await reisorProMarketRequest(`/expeditor/park/${encodeURIComponent(id)}/archive`, { method: "POST", token });
      if (!response?.ok) throw new Error(data?.error || "PARK_ARCHIVE_FAILED");
      setItems((prev) => prev.filter((row) => row.id !== id));
    } catch {
      setError(t.archiveError);
    }
  }

  useEffect(() => {
    let active = true;
    if (!token) return () => { active = false; };

    (async () => {
      try {
        const { response, data } = await reisorProMarketRequest("/expeditor/park", { token });
        if (!response?.ok) throw new Error(data?.error || "PARK_LOAD_FAILED");
        if (active) setItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (active) setError(t.error);
      }
    })();

    return () => { active = false; };
  }, [token, t.error, reloadKey]);

  return (
    <section className="space-y-3" data-reisor-expeditor-park="v1">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-black text-slate-950 dark:text-white">{t.title}</h1>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-black text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">{items.length}</span>
        </div>
        <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">{t.subtitle}</p>
      </header>

      <button type="button" onClick={() => { setEditingItem(null); setShowForm(true); }} className="h-10 rounded-xl bg-blue-600 px-4 text-sm font-black text-white">+ {t.add}</button>

      {showForm ? <ReisorProExpeditorParkForm token={token} item={editingItem} onCancel={() => { setShowForm(false); setEditingItem(null); }} onSaved={() => { setShowForm(false); setEditingItem(null); setReloadKey((v) => v + 1); }} /> : null}

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div> : null}

      {!error && items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-900">{t.empty}</div>
      ) : null}

      {items.map((item) => (
        <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="font-black text-slate-950 dark:text-white">{item.fullName || "—"}</div>
          <div className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">{[item.carBrand, item.plateNumber, item.bodyType].filter(Boolean).join(" · ") || "—"}</div>
          <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/60"><span className="block text-[11px] font-black uppercase text-slate-400">{t.freeAt}</span><strong className="text-slate-900 dark:text-white">{formatDate(item.availableAt, code)}</strong></div>
            <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-950/60"><span className="block text-[11px] font-black uppercase text-slate-400">{t.place}</span><strong className="text-slate-900 dark:text-white">{item.availableLocation || "—"}</strong></div>
          </div>
          <div className="mt-2 text-xs font-bold text-slate-500 dark:text-slate-400">{t.reminder}: {Number(item.reminderLeadHours || 0) ? `${Number(item.reminderLeadHours)} ${t.hourUnit}` : t.noReminder}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {phoneDigits(item.phone) ? <a href={`tel:${phoneDigits(item.phone)}`} className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-800 dark:bg-slate-800 dark:text-slate-100">{t.call}</a> : null}
            {phoneDigits(item.phone) ? <a href={`https://wa.me/${phoneDigits(item.phone)}`} target="_blank" rel="noreferrer" className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-800 dark:bg-slate-800 dark:text-slate-100">{t.whatsapp}</a> : null}
            {telegramUrl(item.telegramUsername) ? <a href={telegramUrl(item.telegramUsername)} target="_blank" rel="noreferrer" className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-800 dark:bg-slate-800 dark:text-slate-100">{t.telegram}</a> : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {typeof onPreparePublish === "function" ? (
              <button
                type="button"
                onClick={() =>
                  onPreparePublish(item)
                }
                className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-black text-white"
              >
                {t.publish}
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => {
                setEditingItem(item);
                setShowForm(true);
              }}
              className="rounded-xl border px-3 py-2 text-xs font-black"
            >
              {t.edit}
            </button>

            <button
              type="button"
              onClick={() =>
                archiveItem(item.id)
              }
              className="rounded-xl border border-red-200 px-3 py-2 text-xs font-black text-red-700 dark:border-red-500/30 dark:text-red-300"
            >
              {t.archive}
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}
